import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import tuncayLogo from "../assets/logo/tuncay.png";
import NoAuthFooter from "../components/NoAuthFooter";
import AlertModal from "../components/AlertModal";
import { IoArrowBackOutline, IoCheckmarkCircleOutline } from "react-icons/io5";

const apiUrl = process.env.REACT_APP_API_URL;

export default function SmsVerification() {
  const navigate = useNavigate();
  const location = useLocation();

  const { responseData } = location.state || {};
  const phone = responseData?.data?.phone || location.state?.phoneNumber || "";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);

  const [alertConfig, setAlertConfig] = useState({
    visible: false,
    title: "",
    message: "",
    type: "warning",
  });

  const showAlert = (title, message, type = "warning") => {
    setAlertConfig({
      visible: true,
      title,
      message,
      type,
    });
  };

  const hideAlert = () => {
    setAlertConfig((prev) => ({ ...prev, visible: false }));
  };

  const inputRefs = useRef([]);

  const maskPhoneNumber = (phoneNum) => {
    if (!phoneNum) return "";
    return phoneNum.replace(/(\d{3})\d+(\d{2})/, "$1****$2");
  };

  useEffect(() => {
    if (!phone && !responseData) {
      navigate("/login", { replace: true });
    }
  }, [phone, responseData, navigate]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];

    if (value.length > 1) {
      const pastedDigits = value.slice(0, 6).split("");
      pastedDigits.forEach((digit, idx) => {
        newOtp[idx] = digit;
      });
      setOtp(newOtp);

      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();

      if (pastedDigits.length === 6) {
        handleVerify(pastedDigits.join(""));
      }
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    const combinedCode = newOtp.join("");
    if (combinedCode.length === 6) {
      handleVerify(combinedCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (manualCode) => {
    const code = manualCode || otp.join("");

    if (code.length !== 6) {
      showAlert(
        "Eksik Kod",
        "Lütfen 6 haneli doğrulama kodunu eksiksiz giriniz.",
        "warning",
      );
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("phone", phone);
      formData.append("sms_code", code);

      const response = await fetch(`${apiUrl}/student/sms-confirm`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      const data = await response.json();

      if (data && data.status) {
        localStorage.setItem("token", data.token);
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }

        navigate("/", { replace: true });
      } else {
        showAlert(
          "Doğrulama Başarısız",
          "Girdiğiniz doğrulama kodu hatalı veya süresi dolmuş.",
          "error",
        );
      }
    } catch (error) {
      showAlert(
        "Bağlantı Hatası",
        "Bir hata oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col antialiased">
      <main className="grow flex items-center justify-center p-4 sm:p-6 w-full">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8 flex flex-col justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-[#1e3a8a] transition-colors mb-6 group cursor-pointer"
            >
              <IoArrowBackOutline className="text-base group-hover:-translate-x-1 transition-transform" />
              Geri Dön
            </button>

            <div className="text-center mb-8">
              <img
                src={tuncayLogo}
                alt="Tuncay Seyahat"
                className="h-16 w-auto mx-auto mb-4"
              />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                SMS Doğrulama
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed px-2">
                <span className="font-semibold text-gray-700">
                  {maskPhoneNumber(phone)}
                </span>{" "}
                numaralı telefonunuza gönderilen 6 haneli doğrulama kodunu
                giriniz.
              </p>
            </div>

            <div className="flex justify-between items-center gap-1.5 sm:gap-2 mb-8">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  disabled={loading}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 sm:w-12 h-12 sm:h-14 text-center text-lg sm:text-xl font-bold text-[#1e3a8a] bg-gray-50 border border-gray-200 rounded-xl focus:border-[#1e3a8a] focus:bg-white focus:ring-2 focus:ring-[#1e3a8a]/20 outline-none transition-all disabled:opacity-50"
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => handleVerify()}
              disabled={loading || otp.join("").length !== 6}
              className={`w-full font-bold py-4 rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-base ${
                !loading && otp.join("").length === 6
                  ? "bg-[#1e3a8a] hover:bg-blue-900 text-white cursor-pointer"
                  : "bg-gray-300 text-white cursor-not-allowed shadow-none"
              }`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Doğrula</span>
                  <IoCheckmarkCircleOutline className="text-xl" />
                </>
              )}
            </button>
          </div>
        </div>
      </main>
      <NoAuthFooter />

      <AlertModal
        visible={alertConfig.visible}
        onClose={hideAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
    </div>
  );
}
